import { ThemeProvider } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import React, { Suspense, useEffect } from "react";
import { NotificationContainer } from "react-notifications";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { logOut } from "./app/features/authSlice";
import { NAV_LIST } from "./app/features/navSlice";
import { useAutoLogout, useSessionCheck } from "./components/Firebase/auth";
import LoadingSpinner from "./components/Loader";
import RequiredAuth from "./components/RequiredAuth";
import RootRedirect from "./components/RootRedirect";
import theme from "./theme";
import FloatingNotifications from "./utils/FloatingNotification";
import { isSecureContext, disableWorkboxLogging, cleanupStaleServiceWorkers } from "./utils/serviceWorkerUtils";

const Register = React.lazy(() => import("./pages/Register"));

const Login = React.lazy(() => import("./pages/Login"));
const Chart = React.lazy(() => import("./pages/Chart"));
const OI_Spurts = React.lazy(() => import("./pages/OI_Spurts"));
const ADV_DEC = React.lazy(() => import("./pages/ADV_DEC"));
const CompAnalysis = React.lazy(() => import("./pages/CompAnalysis"));
const MarketView = React.lazy(() => import("./pages/MarketView"));
const OI = React.lazy(() => import("./pages/OI"));
const SectorMomentum = React.lazy(() => import("./pages/SectorMomentum"));
const ScoreMomentum = React.lazy(() => import("./pages/ScoreMomentum"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Portfolio = React.lazy(() => import("./pages/Portfolio"));
const TableAnalysis = React.lazy(() => import("./pages/TableAnalysis"));
const Subscribers = React.lazy(() => import("./pages/Subscribers"));
const SubscriberDetails = React.lazy(() => import("./pages/SubscriberDetails"));
const SmallCasePerformance = React.lazy(() =>
  import("./pages/SmallCasePerformance")
);
const Research = React.lazy(() => import("./pages/Research"));
const CreateCall = React.lazy(() => import("./pages/Research/CreateCall"));
const UpdateCall = React.lazy(() =>
  import("./pages/Research/UpdateCall/UpdateCall")
);
const SmallCase = React.lazy(() => import("./pages/SmallCase"));
const AdminPanel = React.lazy(() => import("./pages/AdminPanel"));
const ExcelUpdate = React.lazy(() => import("./pages/ExcelUpdate"));
const ScreenerTrends = React.lazy(() => import("./pages/ScreenerTrends"));

LicenseInfo.setLicenseKey(
  process.env.REACT_APP_MUI_LICENSE_KEY || ""
);

function App() {
  useAutoLogout();
  useSessionCheck();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userRole = useSelector((state) => state.auth.userRole);
  const allowedPages = useSelector((state) => state.auth.allowedPages);
  useEffect(() => {
    if (userRole === "admin" || userRole === null) return;
    const pathArray = location.pathname.split("/");
    const lastSegment = pathArray[pathArray.length - 1];
    if (lastSegment === "login" || lastSegment === "register" || lastSegment === "") return;

    // Check for subscriber details route (subscribers/:id)
    if (pathArray.length === 3 && pathArray[1] === "subscribers" && pathArray[2] !== "subscribers") {
      // This is a subscriber detail page (subscribers/:id)
      if (Array.isArray(allowedPages) && !allowedPages?.includes("SUBSCRIBER DETAILS")) {
        if (allowedPages.length > 0) {
          let page = NAV_LIST.filter((el) => el.nav === allowedPages[0]);
          navigate(page[0].path);
        } else {
          FloatingNotifications("error", "Contact admin for access");
          dispatch(logOut());
        }
      }
      return;
    }

    const elem = NAV_LIST.filter((el) => el.path === lastSegment);
    if (Array.isArray(allowedPages) && !allowedPages?.includes(elem[0]?.nav)) {
      if (allowedPages.length > 0) {
        let page = NAV_LIST.filter((el) => el.nav === allowedPages[0]);
        navigate(page[0].path);
      } else {
        FloatingNotifications("error", "Contact admin for access");
        dispatch(logOut());
      }
    }
  }, [allowedPages, location.pathname, userRole]);
  useEffect(() => {
    // Clean up stale service workers first (fixes cached service worker issues)
    // This is especially important when the app works in incognito but not in normal mode
    const initializeApp = async () => {
      try {
        const result = await cleanupStaleServiceWorkers();
        if (result.cleaned && result.unregistered > 0) {
          console.log('✅ Service worker cleanup:', result.message);
          console.log('Reloading page to register fresh service workers...');
          // Reload the page after cleanup to ensure fresh service worker registration
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return; // Exit early if we're reloading
        }
      } catch (error) {
        console.warn('Service worker cleanup failed:', error);
      }

      // Reduce Workbox console noise in development
      if (process.env.NODE_ENV === 'development') {
        setTimeout(() => {
          disableWorkboxLogging();
          console.log('Workbox logging disabled for cleaner console');
        }, 2000);
      }
    };

    initializeApp();
  }, []);

  return (
    <>
      <NotificationContainer />
      <ThemeProvider theme={theme}>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={<RequiredAuth />}>
              <Route index element={<RootRedirect />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="oiSpurts" element={<OI_Spurts />} />
              <Route path="tableAnalysis" element={<TableAnalysis />} />
              <Route path="compAnalysis" element={<CompAnalysis />} />
              <Route path="pieChart" element={<Chart />} />
              <Route path="oi" element={<OI />} />
              <Route path="adv_dec" element={<ADV_DEC />} />
              <Route path="marketView" element={<MarketView />} />
              <Route path="sectorMomentum" element={<SectorMomentum />} />
              <Route path="scoreMomentum" element={<ScoreMomentum />} />
              <Route path="screenerTrends" element={<ScreenerTrends />} />
              <Route path="research" element={<Research />} />
              <Route path="research/createCall" element={<CreateCall />} />
              <Route path="research/updateCall/:id" element={<UpdateCall />} />
              <Route path="smallCase" element={<SmallCase />} />
              <Route
                path="smallCasePerformance"
                element={<SmallCasePerformance />}
              />
              <Route path="subscribers" element={<Subscribers />} />
              <Route path="subscribers/:id" element={<SubscriberDetails />} />
              <Route path="adminPanel" element={<AdminPanel />} />
              <Route path="excelUpdate" element={<ExcelUpdate />} />
            </Route>
          </Routes>
        </Suspense>
      </ThemeProvider>
    </>
  );
}

export default App;
